/**
 * Admin "Edit Points Config" screen. Mirrors `app/owner/subscriptions/points-settings.tsx`
 * with admin impersonation.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Switch, TouchableOpacity } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../../../../src/components/Card';
import Button from '../../../../../src/components/Button';
import Input from '../../../../../src/components/Input';
import Picker from '../../../../../src/components/Picker';
import { useToast } from '../../../../../src/components/Toast';
import ContentContainer from '../../../../../src/components/ContentContainer';
import { useTheme } from '../../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../../src/utils/theme';
import { makeOwnerApi } from '../../../../../src/api/adminAsOwner';

interface RedemptionEntry { points: number; tierId?: string | null; tierName: string | null; freeMinutes: number | null }
interface WashTier { id: string; name: string; priceInCents: number; isActive: boolean }
interface PointsCfg {
  ownerId: string;
  isEnabled: boolean;
  pointsPerDollar: number;
  redemptionTable: RedemptionEntry[];
  expirationMonths: number;
  customerVisibleName: string | null;
}

export default function AdminPointsSettingsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { id: ownerId } = useLocalSearchParams<{ id: string }>();
  const ownerApi = useMemo(() => makeOwnerApi(ownerId ?? ''), [ownerId]);

  const [cfg, setCfg] = useState<PointsCfg | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);

  useEffect(() => {
    if (!ownerId) return;
    (async () => {
      try {
        const c = await ownerApi.get<PointsCfg>('/api/owner/points/config');
        setCfg(c);
        const catalog = await ownerApi.get<WashTier[]>('/api/wash-tiers');
        setWashTiers(catalog.filter(t => t.isActive));
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
  }, [ownerId]);

  if (!cfg) return null;

  const update = (patch: Partial<PointsCfg>) => setCfg({ ...cfg, ...patch });

  const addRow = (kind: 'tier' | 'minutes') => {
    if (kind === 'tier') {
      const first = washTiers[0];
      if (!first) { toast.show({ type: 'error', title: 'No wash tiers', message: 'This owner has no wash tiers yet.' }); return; }
      update({ redemptionTable: [...cfg.redemptionTable, { points: 500, tierId: first.id, tierName: first.name, freeMinutes: null }] });
    } else {
      update({ redemptionTable: [...cfg.redemptionTable, { points: 300, tierId: null, tierName: null, freeMinutes: 5 }] });
    }
  };
  const removeRow = (idx: number) => update({ redemptionTable: cfg.redemptionTable.filter((_, i) => i !== idx) });
  const editRow = (idx: number, patch: Partial<RedemptionEntry>) => {
    update({ redemptionTable: cfg.redemptionTable.map((r, i) => i === idx ? { ...r, ...patch } : r) });
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      await ownerApi.put('/api/owner/points/config', {
        isEnabled: cfg.isEnabled,
        pointsPerDollar: Number(cfg.pointsPerDollar) || 1.0,
        redemptionTable: cfg.redemptionTable.filter(r => r.points > 0 && (r.tierName || r.freeMinutes)),
        expirationMonths: cfg.expirationMonths || 12,
        customerVisibleName: cfg.customerVisibleName || null,
      });
      toast.show({ type: 'success', title: 'Saved' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Text style={styles.adminBanner}>Admin acting on behalf of owner {ownerId}</Text>

        <Card style={styles.formCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionTitle}>Enable rewards program</Text>
              <Text style={styles.hintText}>Customers earn points on paid washes and redeem for free washes.</Text>
            </View>
            <Switch value={cfg.isEnabled} onValueChange={(v) => update({ isEnabled: v })} />
          </View>
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Earn rate</Text>
          <Input label="Points per $1 spent" value={String(cfg.pointsPerDollar)} onChangeText={(t) => update({ pointsPerDollar: parseFloat(t) || 0 })} keyboardType="decimal-pad" placeholder="1" />
          <Input label="Program name (shown to customers)" value={cfg.customerVisibleName ?? ''} onChangeText={(t) => update({ customerVisibleName: t || null })} placeholder="e.g. Kraken Rewards" />
          <Input label="Points expire after (months)" value={String(cfg.expirationMonths)} onChangeText={(t) => update({ expirationMonths: parseInt(t) || 12 })} keyboardType="number-pad" placeholder="12" />
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Redemption options</Text>
          {cfg.redemptionTable.length === 0 && (
            <Text style={styles.hintText}>Add what customers can redeem points for. Each row is one offer.</Text>
          )}
          {cfg.redemptionTable.map((row, idx) => (
            <View key={idx} style={styles.rowEditor}>
              <View style={{ flex: 1, gap: spacing.xs }}>
                <Input label="Points" value={String(row.points)} onChangeText={(t) => editRow(idx, { points: parseInt(t) || 0 })} keyboardType="number-pad" />
                {row.tierName !== null ? (
                  washTiers.length > 0 ? (
                    <Picker label="Wash tier" options={washTiers.map(t => ({ label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})`, value: t.id }))} value={row.tierId ?? ''} onValueChange={(v) => { const t = washTiers.find(x => x.id === v); editRow(idx, { tierId: v, tierName: t?.name ?? row.tierName }); }} />
                  ) : (
                    <Text style={styles.hintText}>Add automatic-bay tiers first.</Text>
                  )
                ) : (
                  <Input label="Free minutes" value={String(row.freeMinutes ?? 0)} onChangeText={(t) => editRow(idx, { freeMinutes: parseInt(t) || 0 })} keyboardType="number-pad" />
                )}
              </View>
              <TouchableOpacity onPress={() => removeRow(idx)} style={styles.removeBtn}>
                <Ionicons name="trash" size={20} color={colors.danger} />
              </TouchableOpacity>
            </View>
          ))}
          <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm }}>
            <TouchableOpacity onPress={() => addRow('tier')} style={styles.addBtn}>
              <Ionicons name="add" size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Add auto tier reward</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => addRow('minutes')} style={styles.addBtn}>
              <Ionicons name="add" size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Add bay minutes</Text>
            </TouchableOpacity>
          </View>
        </Card>

        <Button title="Save" onPress={handleSave} loading={submitting} size="lg" />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.sm },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic' },
  rowEditor: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.md },
  removeBtn: { padding: spacing.sm, justifyContent: 'center' },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.primary },
  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
